<script lang="ts">
  import Readout from '../../lib/components/charts/Readout.svelte';
  import { addDays } from '../../lib/utils/chart';
  import { today } from '../../lib/utils/insights';
  import { weekStart } from '../../lib/utils/progress';
  import { shade, yearGrid } from '../../lib/utils/training';
  import { month, t, weekday } from '../../lib/i18n/index.ts';
  import { dayName } from './format';

  // YearDays is a year of the timeline a square a day, as the Progress
  // calendar draws it (spec 006 FR-093): week columns and weekday rows
  // from the member's first day of the week, shaded in five steps relative
  // to max. As every chart does (spec 010 FR-033), hovering a square
  // reads it, and tapping one or moving with the arrow keys selects it,
  // whose button opens its day. binary draws days as with or without, as a
  // place's visit days are.
  let { year, values, max, color, text, name, onOpen, binary = false }: {
    year: number;
    values: Map<string, number>;
    max: number;
    color: string;
    // text writes a day's value for the readout and screen readers.
    text: (v: number | undefined) => string;
    name: string;
    onOpen: (day: string) => void;
    binary?: boolean;
  } = $props();

  const ws = weekStart(navigator.language);
  const now = today();

  let hover = $state<string | null>(null);
  let picked = $state<string | null>(null);
  let gridEl: HTMLElement | undefined = $state();
  let scroller: HTMLElement | undefined = $state();

  // A narrow window shows part of the year, so it opens this year's at today.
  $effect(() => {
    if (scroller && String(year) === now.slice(0, 4)) scroller.scrollLeft = scroller.scrollWidth;
  });

  $effect(() => {
    if (picked && !picked.startsWith(String(year))) picked = null;
  });

  const grid = $derived(yearGrid(year, ws));
  const months = $derived(grid.map((col) => {
    const first = col.find((d) => d?.endsWith('-01'));
    return first ? month(new Date(`${first}T12:00:00`), true) : '';
  }));
  const rows = Array.from({ length: 7 }, (_, r) => weekday(new Date(2023, 0, 1 + ((ws + r) % 7), 12), true));

  function fill(step: number): string {
    if (step <= 0) return 'var(--theme-surface1)';
    return `color-mix(in srgb, ${color} ${[0, 30, 55, 78, 100][step]}%, var(--theme-surface0))`;
  }

  const label = (day: string) => t('timelineReading', { label: dayName(day), value: text(values.get(day)) });

  // focusDay is the one square in the tab order: the selected day, else
  // the year's last day so far.
  const focusDay = $derived(picked ?? (String(year) === now.slice(0, 4) ? now : `${year}-12-31`));

  // key moves the selection a week across or a day down, staying in the year
  // and out of the future, and focuses the new square.
  function key(e: KeyboardEvent, day: string) {
    const by: Record<string, number> = { ArrowLeft: -7, ArrowRight: 7, ArrowUp: -1, ArrowDown: 1 };
    if (e.key === 'Escape') {
      picked = null;
      e.preventDefault();
      return;
    }
    if (!(e.key in by)) return;
    e.preventDefault();
    const next = addDays(day, by[e.key]);
    if (!next.startsWith(String(year)) || next > now) return;
    picked = next;
    gridEl?.querySelector<HTMLElement>(`[data-day="${next}"]`)?.focus();
  }
</script>

<div class="space-y-2">
  <Readout title={hover ?? picked ? label((hover ?? picked)!) : t('timelineHoverDay')} idle={!(hover ?? picked)} items={[]}>
    {#snippet action()}
      {#if picked}
        <button class="btn btn-secondary btn-sm" onclick={() => onOpen(picked!)}>{t('timelineOpenDay')}</button>
      {/if}
    {/snippet}
  </Readout>
  <div bind:this={scroller} class="overflow-x-auto pb-1">
    <div bind:this={gridEl} class="inline-flex gap-[3px] text-xs text-text-muted" role="group" aria-label={name} onmouseleave={() => (hover = null)}>
      <div class="flex flex-col gap-[3px] pr-1 pt-4" aria-hidden="true">
        {#each rows as r, i (i)}<span class="h-[11px] leading-[11px] text-[11px]">{i % 2 === 1 ? r : ''}</span>{/each}
      </div>
      {#each grid as col, c (c)}
        <div class="flex flex-col gap-[3px]">
          <span class="h-[13px] leading-[13px] text-[11px] whitespace-nowrap overflow-visible w-[11px]" aria-hidden="true">{months[c]}</span>
          {#each col as day, r (r)}
            {#if day && day <= now}
              {@const step = shade(values.get(day) ?? 0, max)}
              <button class="w-[11px] h-[11px] rounded-[2px] hover:ring-1 {picked === day ? 'ring-2' : ''}" data-day={day} tabindex={day === focusDay ? 0 : -1}
                style="background: {fill(step)}; --tw-ring-color: var(--theme-text);"
                aria-label={label(day)} aria-pressed={picked === day} onmouseenter={() => (hover = day)} onclick={() => (picked = day)} onkeydown={(e) => key(e, day)}></button>
            {:else}
              <span class="w-[11px] h-[11px]" aria-hidden="true"></span>
            {/if}
          {/each}
        </div>
      {/each}
    </div>
  </div>
  <div class="flex flex-wrap items-center justify-end gap-2 text-xs text-text-muted" aria-hidden="true">
    {#if binary}
      <span class="flex items-center gap-1"><span class="w-[11px] h-[11px] rounded-[2px]" style="background: {fill(0)};"></span>{t('timelineNoVisit')}</span>
      <span class="flex items-center gap-1 ml-2"><span class="w-[11px] h-[11px] rounded-[2px]" style="background: {fill(4)};"></span>{t('timelineVisit')}</span>
    {:else}
      <span class="flex items-center gap-1">
        {t('timelineNone')}
        {#each [0, 1, 2, 3, 4] as s (s)}<span class="w-[11px] h-[11px] rounded-[2px]" style="background: {fill(s)};"></span>{/each}
        {t('timelineMore')}
      </span>
    {/if}
  </div>
</div>
